/* eslint-disable import/no-extraneous-dependencies -- for adobe spectrum, aria, stately. */
import { Heading } from '@eleveo/components-react';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { PressResponder } from '@react-aria/interactions';
import {
  useOverlayTrigger,
  useOverlay,
  useModal,
  DismissButton,
  OverlayContainer,
  useOverlayPosition,
  AriaPositionProps,
} from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import { useOverlayTriggerState } from '@react-stately/overlays';
import clsx from 'clsx';
import React, { LegacyRef, useRef } from 'react';

type OverlayContentProps = {
  children: React.ReactNode;
  onClose: () => void;
  title?: string;
  customClassName?: string;
  isOpen?: boolean;
};

const OverlayContent = React.forwardRef<HTMLElement, OverlayContentProps>((props, ref) => {
  const { onClose, isOpen, title, children, customClassName, ...otherProps } = props;

  const testRef = ref as React.MutableRefObject<HTMLElement>;

  const { overlayProps } = useOverlay(
    {
      onClose,
      isOpen,
      isDismissable: true,
      shouldCloseOnBlur: false,
    },
    testRef
  );

  // Hide content outside the modal from screen readers.
  const { modalProps } = useModal();

  const { dialogProps, titleProps } = useDialog({}, testRef);

  return (
    <OverlayContainer>
      <FocusScope autoFocus restoreFocus contain>
        <div
          className={clsx('integrations-overlay-wrapper', customClassName)}
          {...mergeProps(overlayProps, dialogProps, otherProps, modalProps)}
          ref={testRef as LegacyRef<HTMLDivElement>}
        >
          {title && (
            <Heading as="h3" {...titleProps} className={clsx('integrations-overlay-wrapper__title', customClassName)}>
              {title}
            </Heading>
          )}
          {children}
          <DismissButton onDismiss={onClose} />
        </div>
      </FocusScope>
    </OverlayContainer>
  );
});

export interface OverlayTriggerProps {
  triggerElement: React.ReactNode;
  title?: string;
  placement?: AriaPositionProps['placement'];
  onOpenChange?: (isOpen: boolean) => void;
  isOpen?: boolean;
  offset?: number;
  crossOffset?: number;
  className?: string;
}

export const OverlayTrigger: React.FC<React.PropsWithChildren<OverlayTriggerProps>> = ({
  triggerElement,
  onOpenChange,
  isOpen,
  placement,
  offset,
  crossOffset,
  title,
  children,
  className,
}) => {
  const triggerRef = useRef<HTMLElement>(null);
  const overlayRef = useRef<HTMLElement>(null);

  const triggerState = useOverlayTriggerState({ onOpenChange, isOpen });
  const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, triggerState, triggerRef);

  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement: placement || 'right top',
    offset: offset ?? 0,
    crossOffset: crossOffset ?? 0,
    isOpen,
  });

  return (
    <>
      <PressResponder {...triggerProps} ref={triggerRef} onPress={triggerState.toggle}>
        {triggerElement}
      </PressResponder>

      {triggerState.isOpen && (
        <OverlayContent
          {...overlayProps}
          {...positionProps}
          customClassName={className}
          title={title}
          onClose={() => triggerState.close()}
          isOpen={triggerState.isOpen}
          ref={overlayRef}
        >
          {children}
        </OverlayContent>
      )}
    </>
  );
};
